import { useEffect, useState } from 'react';

type WindowWithWebkitAudioContext = Window & {
	webkitAudioContext?: typeof AudioContext;
};

const resolveAudioContextClass = (): typeof AudioContext | undefined =>
	window.AudioContext ||
	(window as WindowWithWebkitAudioContext).webkitAudioContext;

type UseMergedAudioVisualizationStreamOptions = {
	micStream: MediaStream | null;
	systemStream: MediaStream | null;
	enabled: boolean;
};

export const useMergedAudioVisualizationStream = ({
	micStream,
	systemStream,
	enabled,
}: UseMergedAudioVisualizationStreamOptions): MediaStream | null => {
	const [mergedStream, setMergedStream] = useState<MediaStream | null>(null);

	useEffect(() => {
		if (!enabled) {
			setMergedStream(null);
			return;
		}

		const hasMic =
			micStream != null && micStream.getAudioTracks().length > 0;
		const hasSystem =
			systemStream != null && systemStream.getAudioTracks().length > 0;

		if (hasMic && hasSystem) {
			const AudioContextClass = resolveAudioContextClass();
			if (!AudioContextClass) {
				setMergedStream(micStream);
				return;
			}

			const audioContext = new AudioContextClass();
			const destination = audioContext.createMediaStreamDestination();
			const micSource = audioContext.createMediaStreamSource(micStream);
			const systemSource =
				audioContext.createMediaStreamSource(systemStream);
			micSource.connect(destination);
			systemSource.connect(destination);
			void audioContext.resume();
			setMergedStream(destination.stream);

			return () => {
				micSource.disconnect();
				systemSource.disconnect();
				void audioContext.close();
				setMergedStream(null);
			};
		}

		setMergedStream(hasMic ? micStream : hasSystem ? systemStream : null);
		return;
	}, [enabled, micStream, systemStream]);

	return mergedStream;
};
